import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { ArtFrame, Bindi, Reveal, ScallopStrip } from "@/components/motifs";
import { fetchArtwork, fetchArtworks } from "@/lib/api";
import { ARTIST, assetUrl } from "@/lib/constants";
import { HOME, PAGES, artworkAlt, artworkPath } from "@/lib/site";
import { artworkSchema } from "@/lib/schema";
import { scrollTop } from "@/lib/smooth";

export default function ArtworkDetail() {
  const { id } = useParams();
  const [art, setArt] = useState(null);
  const [all, setAll] = useState([]);
  const [missing, setMissing] = useState(false);
  // Only a 404 means the artwork is gone; a sleeping or unreachable API must not
  // tell search engines to drop the page.
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    scrollTop(true);
    setArt(null);
    setMissing(false);
    setNotFound(false);
    fetchArtwork(id)
      .then(setArt)
      .catch((err) => {
        setMissing(true);
        setNotFound(err.response?.status === 404);
      });
    fetchArtworks()
      .then(setAll)
      .catch(() => {});
  }, [id]);

  const related = useMemo(() => {
    if (!art) return [];
    const themes = art.themes || [];
    return all.filter((a) => a.id !== art.id && (a.themes || []).some((t) => themes.includes(t))).slice(0, 3);
  }, [art, all]);

  const jsonLd = useMemo(() => (art ? artworkSchema(art) : undefined), [art]);
  const trail = [
    { name: HOME.name, path: "/" },
    { name: PAGES.gallery.name, path: PAGES.gallery.path },
    ...(art ? [{ name: art.title, path: artworkPath(art) }] : []),
  ];

  return (
    <div data-testid="artwork-detail-page">
      <Seo
        title={art ? `${art.title} — Mithila Painting by ${ARTIST.name}` : `${ARTIST.name} — Artwork`}
        siteName={ARTIST.name}
        description={art?.intro || `An original Mithila / Madhubani artwork by ${ARTIST.name}.`}
        image={art ? assetUrl(art.image) : "/assets/logo.png"}
        imageAlt={art ? artworkAlt(art) : undefined}
        type={art ? "article" : "website"}
        robots={notFound ? "noindex, follow" : undefined}
        jsonLd={jsonLd}
      />
      <Navbar />

      <main className="mx-auto max-w-6xl px-5 pb-24 pt-32 sm:px-8">
        <Reveal>
          <Breadcrumbs trail={trail} />
        </Reveal>

        {missing && (
          <div className="flex flex-col items-center gap-4 py-32 text-center">
            <Bindi size={10} pulse />
            <p className="font-serif text-2xl italic text-muted-foreground">This artwork could not be found.</p>
          </div>
        )}

        {!art && !missing && (
          <div className="flex flex-col items-center gap-4 py-32">
            <Bindi size={10} pulse />
            <p className="eyebrow text-muted-foreground">Preparing the catalogue page…</p>
          </div>
        )}

        {art && (
          <>
            <Reveal className="mt-12">
              <ArtFrame className="mx-auto w-fit max-w-4xl">
                <img
                  src={assetUrl(art.image)}
                  alt={artworkAlt(art)}
                  width={art.width}
                  height={art.height}
                  className="mx-auto block max-h-[82vh] w-auto max-w-full object-contain"
                  data-testid="artwork-detail-image"
                />
              </ArtFrame>
            </Reveal>

            <div className="mx-auto mt-20 max-w-3xl">
              <Reveal>
                <p className="eyebrow flex items-center gap-3 text-accent">
                  <Bindi size={7} /> {(art.themes || []).join(" · ") || "Mithila / Madhubani"}
                </p>
                <h1 className="mt-4 font-serif text-4xl font-semibold leading-tight tracking-tight sm:text-5xl" data-testid="artwork-detail-title">
                  {art.title}
                </h1>
                {art.intro && (
                  <p className="mt-6 font-serif text-xl italic leading-relaxed text-muted-foreground sm:text-2xl">
                    {art.intro}
                  </p>
                )}
                {(art.medium || art.year || art.dimensions) && (
                  <p className="eyebrow mt-6 text-muted-foreground">
                    {[art.medium, art.year, art.dimensions].filter(Boolean).join(" · ")}
                  </p>
                )}
              </Reveal>

              <ScallopStrip className="my-14 text-foreground/20" />

              {(art.story || []).length > 0 && (
                <Reveal>
                  <h2 className="font-serif text-2xl font-medium tracking-tight sm:text-3xl">The Story</h2>
                  <div className="mt-6 space-y-5">
                    {art.story.map((p, i) => (
                      <p key={i} className="text-base leading-relaxed text-muted-foreground">{p}</p>
                    ))}
                  </div>
                </Reveal>
              )}

              {((art.visual_language || []).length > 0 || art.cultural_context) && (
                <Reveal className="mt-16">
                  <h2 className="font-serif text-2xl font-medium tracking-tight sm:text-3xl">Reading the Artwork</h2>
                  {art.cultural_context && (
                    <p className="mt-6 text-base leading-relaxed text-muted-foreground">{art.cultural_context}</p>
                  )}
                  {(art.visual_language || []).length > 0 && (
                    <div className="mt-6 flex flex-wrap gap-2">
                      {art.visual_language.map((v) => (
                        <span key={v} className="chip">{v}</span>
                      ))}
                    </div>
                  )}
                </Reveal>
              )}

              {(art.motifs || []).length > 0 && (
                <Reveal className="mt-16">
                  <h2 className="font-serif text-2xl font-medium tracking-tight sm:text-3xl">Mithila Elements</h2>
                  <div className="mt-6 flex flex-wrap gap-2">
                    {art.motifs.map((m) => (
                      <span key={m} className="chip">
                        <Bindi size={5} /> {m}
                      </span>
                    ))}
                  </div>
                </Reveal>
              )}

              <Reveal className="mt-16 border-t border-border pt-8">
                <p className="font-mono text-[10px] uppercase leading-relaxed tracking-[0.18em] text-muted-foreground">
                  Catalogue note written with AI assistance from the artwork itself, reviewed and
                  editable by me. N° {String(art.order || 0).padStart(2, "0")}
                </p>
              </Reveal>
            </div>

            {related.length > 0 && (
              <div className="mt-24">
                <Reveal>
                  <h2 className="mb-10 font-serif text-2xl font-medium tracking-tight sm:text-3xl">Related Works</h2>
                </Reveal>
                <div className="grid gap-10 sm:grid-cols-3">
                  {related.map((r, i) => (
                    <Reveal key={r.id} delay={0.07 * i}>
                      <Link to={`/artwork/${r.id}`} className="group block" data-testid={`related-artwork-${i}`}>
                        <div className="overflow-hidden border border-foreground/15 bg-paper p-2.5 transition-colors duration-500 group-hover:border-foreground/40">
                          <img
                            src={assetUrl(r.image)}
                            alt={artworkAlt(r)}
                            loading="lazy"
                            className="block h-auto w-full transition-transform duration-700 group-hover:scale-[1.02]"
                          />
                        </div>
                        <div className="mt-3 flex items-center justify-between px-1">
                          <p className="font-serif text-lg font-medium tracking-tight transition-colors group-hover:text-accent">{r.title}</p>
                          <ArrowUpRight size={14} className="text-muted-foreground transition-all group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-accent" />
                        </div>
                      </Link>
                    </Reveal>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
